import React from 'react';
import { useAuth } from '../../context/AuthContext';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ isOpen, onClose }) => {
  const { branding } = useAuth();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    window.open('mailto:hello@adroitai.ai?subject=Adroit%20Trial', '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-sm p-8 shadow-2xl text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-12 mx-auto mb-4 rounded-xl flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${branding.primary_color}30, ${branding.secondary_color}30)` }}
        >
          <svg className="w-6 h-6" style={{ color: branding.primary_color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-white mb-2">Unlock Adroit</h3>
        <p className="text-slate-400 text-sm mb-6">
          Upload, search, analyse, and share.
        </p>

        <button
          onClick={handleUpgrade}
          className="w-full py-3 text-white font-semibold rounded-lg transition-all hover:shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`,
            boxShadow: `0 4px 12px ${branding.primary_color}40`
          }}
        >
          Start 30-day trial
        </button>

        <p className="text-slate-500 text-xs mt-3">
          Full access for 30 days. Then subscription to continue.
        </p>

        <button
          onClick={onClose}
          className="mt-4 text-sm text-slate-500 hover:text-slate-300 transition-colors"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default UpgradePrompt;

// Tooltip wrapper for greyed-out features
interface GuestGateProps {
  children: React.ReactNode;
  tooltip: string;
  enabled: boolean;  // true = feature is available, false = greyed out
  onUpgrade?: () => void;
}

export const GuestGate: React.FC<GuestGateProps> = ({ children, tooltip, enabled, onUpgrade }) => {
  if (enabled) return <>{children}</>;

  return (
    <div className="relative group">
      <div className="opacity-40 pointer-events-none select-none">
        {children}
      </div>
      <div
        className="absolute inset-0 cursor-pointer"
        onClick={onUpgrade}
        title={tooltip}
      />
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-xs text-slate-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 max-w-[200px] text-center">
        {tooltip}
        {onUpgrade && (
          <span className="block text-teal-400 mt-1 text-[10px]">Click to upgrade</span>
        )}
      </div>
    </div>
  );
};
